import {
  summarizePattern,
  formatRowValue,
  OPERATOR_LABELS,
} from '../../pages/admin/pattern-helpers';
import type { EventSubscriptionFilter } from '../../types';

interface SubscriptionFilterSummaryProps {
  filter: EventSubscriptionFilter;
}

/**
 * Read-only disclosure for a subscription's matching rule (`filter.pattern`).
 * Decodes the nested EventPattern into `path · operator · value` lines via the
 * shared `summarizePattern` helper. Any leaf the decoder can't model is shown
 * verbatim as raw JSON so nothing silently disappears. Renders nothing when the
 * subscription carries no (meaningful) pattern.
 */
export function SubscriptionFilterSummary({
  filter,
}: SubscriptionFilterSummaryProps) {
  if (!filter.pattern) return null;

  const { rows, pinnedEventTypes, unparseable } = summarizePattern(
    filter.pattern,
  );

  // Only surface pinned event types that aren't already shown as the row's
  // header eventType — avoids echoing the same value twice.
  const extraEventTypes = pinnedEventTypes.filter(
    (et) => et !== filter.eventType,
  );

  const hasContent =
    rows.length > 0 || extraEventTypes.length > 0 || !!unparseable;
  if (!hasContent) return null;

  return (
    <details className="mt-1.5 text-xs">
      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
        Match rule
      </summary>
      <div className="mt-1.5 space-y-2">
        {extraEventTypes.length > 0 && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-muted-foreground">Event types:</span>
            {extraEventTypes.map((et) => (
              <span
                key={et}
                className="font-mono bg-muted px-1.5 py-0.5 rounded"
              >
                {et}
              </span>
            ))}
          </div>
        )}

        {rows.length > 0 && (
          <ul className="space-y-1">
            {rows.map((row) => (
              <li
                key={row.id}
                className="flex flex-wrap items-baseline gap-1.5"
              >
                <code className="font-mono bg-muted px-1.5 py-0.5 rounded">
                  {row.fieldPath}
                </code>
                {row.operator === 'exists' ? (
                  <span className="text-foreground">{formatRowValue(row)}</span>
                ) : (
                  <>
                    <span className="text-muted-foreground">
                      {OPERATOR_LABELS[row.operator]}
                    </span>
                    <span className="text-foreground">
                      {formatRowValue(row)}
                    </span>
                  </>
                )}
              </li>
            ))}
          </ul>
        )}

        {unparseable && (
          <pre className="rounded-md border bg-muted/30 p-2 overflow-x-auto">
            {JSON.stringify(unparseable, null, 2)}
          </pre>
        )}
      </div>
    </details>
  );
}
